import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { leadsAPI } from '../services/api';
import { formatStatusLabel } from '../utils/formatters';

const LeadSearchBar = ({ onResultSelect, initialValue = '', onSearchTermChange }) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Update parent when search term changes, so it can sync with URL or other states if needed
  useEffect(() => {
    if (onSearchTermChange) {
      onSearchTermChange(searchTerm);
    }
  }, [searchTerm, onSearchTermChange]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchTerm.trim().length >= 2) {
        setLoading(true);
        try {
          // Add this endpoint in api.js: get('/leads/search', { params: { q: term } })
          const res = await leadsAPI.searchLeads({ q: searchTerm.trim() });
          setResults(res.data.leads || []);
          setIsOpen(true);
        } catch (error) {
          console.error('Failed to search leads:', error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSelect = (lead) => {
    setIsOpen(false);
    if (onResultSelect) {
      onResultSelect(lead);
    }
  };

  const maskPhone = (phone) => {
    if (!phone) return '-';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length >= 10) {
      return digits.slice(0, 4) + '-xxxx-' + digits.slice(-2);
    }
    return 'xxxx-xxxx';
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {loading ? (
          <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
        ) : (
          <Search className="h-5 w-5 text-gray-400" />
        )}
      </div>
      <input
        type="text"
        placeholder="Search by name, phone prefix, notes, project..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => {
          if (searchTerm.trim().length >= 2 && results.length > 0) {
            setIsOpen(true);
          }
        }}
        className="block w-full h-9 !pl-10 pr-3 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-gray-200 text-sm placeholder:text-gray-400"
      />

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-100 max-h-96 overflow-y-auto">
          {results.length > 0 ? (
            <ul className="py-2">
              {results.map((lead) => (
                <li key={lead.id}>
                  <button
                    onClick={() => handleSelect(lead)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex flex-col gap-1 border-b border-gray-50 last:border-0 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900 text-sm">{lead.name}</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {formatStatusLabel(lead.status)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{maskPhone(lead.phone)}</span>
                      {lead.project_name && (
                        <>
                          <span>&bull;</span>
                          <span>{lead.project_name}</span>
                        </>
                      )}
                    </div>
                    {lead.matched_note && (
                      <div className="text-xs text-gray-400 mt-1 italic truncate">
                        Note: "...{lead.matched_note}..."
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-sm text-gray-500 text-center">
              No leads found matching "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadSearchBar;
